import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  RotateCcw,
  AlertTriangle,
  CheckCircle,
  HelpCircle,
  Sparkles,
  Phone,
  // Icones dynamiques referencees par le tree backend
  Turtle,
  AlertCircle,
  HardDrive,
  Monitor,
  MonitorSmartphone,
  Printer,
  Globe,
  ShoppingCart,
  MessageCircle,
  Laptop,
  Cpu,
  Wifi,
  Power,
  Home,
  Mail,
  Database,
  Settings,
  // Icones B2B
  AlertOctagon,
  Shield,
  Server,
  Brain,
  GraduationCap,
} from 'lucide-react';
import AppointmentCalendar from '../components/AppointmentCalendar';
import AppointmentForm from '../components/AppointmentForm';
import CallbackB2BForm from '../components/CallbackB2BForm';
import EmergencyPhoneCTA from '../components/EmergencyPhoneCTA';
import SEO from '../components/SEO';
import { SEO_TEXTES_HORS_PRERENDU } from '../data/seoTextes';
import { fetchConfiguratorStep } from '../lib/configurator';
import { resolveFormUrl } from '../lib/configuratorMapping';
import { useLang, t, getLocale, i18nField } from '../context/LanguageContext';
import translations from '../i18n/translations';

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-50px' },
  transition: { duration: 0.5, ease: 'easeOut' },
};

const slideRight = {
  initial: { opacity: 0, x: 16 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -16 },
  transition: { duration: 0.3, ease: 'easeOut' },
};

// Mapping nom de l'icone (string renvoyee par le backend) -> composant lucide-react.
const ICON_MAP = {
  Turtle,
  AlertCircle,
  HardDrive,
  Monitor,
  MonitorSmartphone,
  Printer,
  Globe,
  ShoppingCart,
  MessageCircle,
  HelpCircle,
  Laptop,
  Cpu,
  Wifi,
  Phone,
  Power,
  Home,
  Mail,
  Database,
  Settings,
  CheckCircle,
  // B2B
  AlertOctagon,
  Shield,
  Sparkles,
  Server,
  Brain,
  GraduationCap,
};

function resolveIcon(name) {
  if (!name) return HelpCircle;
  return ICON_MAP[name] || HelpCircle;
}


// Phase 5 (2026-05-22) : alias des types qui ont allow_custom_duration=true
// cote backend. Quand le configurateur recommande un de ces types, on redirige
// vers /prendre-rdv?type=<alias> qui sait afficher le picker durée plutot que
// d'inliner un calendrier 15 min par défaut. À synchroniser manuellement avec
// le seed backend si on ajoute d'autres types custom-duration plus tard.
const CUSTOM_DURATION_ALIASES = new Set(['personnalise']);

export default function Configurateur() {
  const navigate = useNavigate();
  const { lang } = useLang();
  const tc = translations.configurateur;
  const [step, setStep] = useState(null);
  const [stack, setStack] = useState([]);
  const [treePath, setTreePath] = useState([]);
  const [leafResult, setLeafResult] = useState(null);
  const [slot, setSlot] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadStep = useCallback(async (stepId) => {
    setLoading(true);
    setError(null);
    setLeafResult(null);
    setSlot(null);
    setConfirmation(null);
    try {
      const data = await fetchConfiguratorStep(stepId);
      setStep(data);
    } catch (e) {
      setError(e.message || t(tc.error_loading, lang));
    } finally {
      setLoading(false);
    }
  }, [lang, tc.error_loading]);

  useEffect(() => {
    loadStep('root');
  }, [loadStep]);

  const handleRestart = () => {
    setStack([]);
    setTreePath([]);
    loadStep('root');
  };

  const handleBack = async () => {
    if (leafResult || slot) {
      setLeafResult(null);
      setSlot(null);
      return;
    }
    if (stack.length === 0) return;
    const previousId = stack[stack.length - 1];
    setStack(stack.slice(0, -1));
    await loadStep(previousId);
  };

  const handleAnswer = async (answer) => {
    const answerLabel = i18nField(answer, 'label', lang);
    const answerNotice = i18nField(answer, 'notice', lang);
    if (answer.next_step_id) {
      setStack([...stack, step.step_id]);
      setTreePath((prev) => [...prev, answerLabel]);
      await loadStep(answer.next_step_id);
      return;
    }
    const newPath = [...treePath, answerLabel];
    setTreePath(newPath);

    if (answer.end_action === 'callback_b2b') {
      setLeafResult({
        kind: 'callback_b2b',
        context_tag: answer.context_tag || null,
      });
      return;
    }
    if (answer.end_action === 'emergency_phone_cta') {
      setLeafResult({ kind: 'emergency_phone_cta' });
      return;
    }
    if (answer.recommend_type_alias) {
      // Phase 5 : redirection vers /prendre-rdv?type=X si type custom-duration.
      if (CUSTOM_DURATION_ALIASES.has(answer.recommend_type_alias)) {
        navigate(`/prendre-rdv?type=${encodeURIComponent(answer.recommend_type_alias)}`);
        return;
      }
      setLeafResult({
        type: 'recommend',
        typeAlias: answer.recommend_type_alias,
        notice: answerNotice || null,
        label: answerLabel,
      });
      return;
    }
    if (answer.end_action === 'callback') {
      setLeafResult({
        type: 'callback',
        notice: answerNotice || null,
        label: answerLabel,
      });
      return;
    }
    if (answer.end_action === 'external_form' && answer.form_id) {
      setLeafResult({
        type: 'external_form',
        formId: answer.form_id,
        notice: answerNotice || null,
        label: answerLabel,
      });
      return;
    }
    setLeafResult({ type: 'unknown', label: i18nField(answer, 'label', lang) });
  };

  const handleSelectSlot = (s) => setSlot(s);
  const handleBookingSuccess = (data) => setConfirmation(data);

  const stepNumber = stack.length + 1;
  const canGoBack = stack.length > 0 || leafResult !== null || slot !== null;

  const renderNotice = (notice) =>
    notice ? (
      <div
        className="bg-amber-500/10 border border-amber-500/30 backdrop-blur-sm rounded-lg p-4"
        data-testid="configurator-notice"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle
            size={20}
            className="text-amber-300 flex-shrink-0 mt-0.5"
          />
          <p className="text-sm text-amber-200 leading-relaxed">{notice}</p>
        </div>
      </div>
    ) : null;

  // Cle utilisee pour animer la transition de step.
  const stepKey = leafResult
    ? `leaf-${leafResult.kind || leafResult.type}-${leafResult.typeAlias || leafResult.formId || leafResult.context_tag || ''}`
    : step?.step_id || 'init';

  return (
    <>
      <SEO {...SEO_TEXTES_HORS_PRERENDU['/configurateur']} path="/configurateur" />
      <main data-testid="configurateur-page">
        {/* Hero */}
        <section className="pt-32 pb-12">
          <div className="max-w-3xl mx-auto px-6 md:px-12">
            <motion.div {...fadeUp}>
              <p className="text-sm font-medium tracking-[0.2em] uppercase text-[#0099D6] mb-4">
                {t(tc.eyebrow, lang)}
              </p>
              <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-white mb-6">
                {t(tc.title, lang)}
              </h1>
              <p className="text-lg md:text-xl text-slate-300 leading-relaxed mb-3">
                {t(tc.subtitle, lang)}
              </p>
              <p className="text-sm text-slate-400">
                {t(tc.already_know, lang)}{' '}
                <Link
                  to="/prendre-rdv"
                  className="text-[#0099D6] hover:underline font-semibold"
                >
                  {t(tc.go_direct, lang)}
                </Link>
              </p>
            </motion.div>
          </div>
        </section>

        {/* Card */}
        <section className="pb-24">
          <div className="max-w-3xl mx-auto px-6 md:px-12">
            <motion.div
              {...fadeUp}
              className="bg-[#15171E]/80 backdrop-blur-xl border border-white/5 rounded-2xl p-6 md:p-10 shadow-[0_0_30px_rgba(0,153,214,0.05)]"
              data-testid="configurateur-container"
            >
              {/* Header etape + actions */}
              {!loading && !error && (step || leafResult) && (
                <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#0099D6] font-semibold">
                    <Sparkles size={14} />
                    {leafResult ? (
                      <span>{t(tc.recommendation, lang)}</span>
                    ) : (
                      <span>{t(tc.step, lang)} {stepNumber}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-4">
                    {canGoBack && (
                      <button
                        type="button"
                        onClick={handleBack}
                        className="flex items-center gap-1 text-[#0099D6] hover:underline text-sm font-medium"
                        data-testid="configurator-back"
                      >
                        <ArrowLeft size={16} />
                        {t(tc.back, lang)}
                      </button>
                    )}
                    {(leafResult || stack.length > 0) && (
                      <button
                        type="button"
                        onClick={handleRestart}
                        className="flex items-center gap-1 text-slate-400 hover:text-white text-sm font-medium"
                        data-testid="configurator-restart"
                      >
                        <RotateCcw size={16} />
                        {t(tc.restart, lang)}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Loading */}
              {loading && (
                <p
                  className="text-center py-12 text-slate-400 text-sm"
                  data-testid="configurator-loading"
                >
                  {t(tc.loading, lang)}
                </p>
              )}

              {/* Error */}
              {error && (
                <div className="text-center py-8" data-testid="configurator-error">
                  <p className="text-red-300 bg-red-500/10 border border-red-400/30 p-4 rounded-lg text-sm font-medium mb-4">
                    {error}
                  </p>
                  <button
                    type="button"
                    onClick={handleRestart}
                    className="bg-[#0099D6] hover:bg-[#0284C7] text-white font-semibold py-2.5 px-6 rounded-lg transition-all hover:scale-[1.02] active:scale-95"
                  >
                    {t(tc.retry, lang)}
                  </button>
                </div>
              )}

              <AnimatePresence mode="wait">
                {/* Question affichee */}
                {!loading && !error && step && !leafResult && (
                  <motion.div
                    key={stepKey}
                    {...slideRight}
                    data-testid="configurator-question"
                  >
                    <h2 className="text-2xl md:text-3xl font-bold text-white mb-2 leading-tight">
                      {i18nField(step, 'question', lang)}
                    </h2>
                    {i18nField(step, 'helper_text', lang) && (
                      <p className="text-slate-400 text-sm mb-6">{i18nField(step, 'helper_text', lang)}</p>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                      {(step.answers || []).map((answer, idx) => {
                        const Icon = resolveIcon(answer.icon);
                        return (
                          <button
                            key={`${step.step_id}-${idx}`}
                            type="button"
                            onClick={() => handleAnswer(answer)}
                            className="group flex items-start gap-4 p-5 rounded-2xl bg-white/[0.02] border border-white/10 hover:border-[#0099D6]/60 hover:bg-[#0099D6]/5 backdrop-blur-sm transition-all text-left active:scale-[0.98] hover:shadow-[0_0_20px_rgba(0,153,214,0.25)]"
                            data-testid={`configurator-answer-${idx}`}
                          >
                            <div className="w-11 h-11 rounded-xl bg-[#0099D6]/10 group-hover:bg-[#0099D6] text-[#0099D6] group-hover:text-white flex items-center justify-center transition-colors flex-shrink-0">
                              <Icon size={22} />
                            </div>
                            <div className="flex-1 pt-0.5">
                              <h3 className="font-semibold text-base text-white leading-snug">
                                {i18nField(answer, 'label', lang)}
                              </h3>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </motion.div>
                )}

                {/* Feuille : recommendation */}
                {!loading &&
                  !error &&
                  leafResult &&
                  leafResult.type === 'recommend' &&
                  !confirmation && (
                    <motion.div
                      key={stepKey}
                      {...slideRight}
                      data-testid="configurator-recommend"
                    >
                      {!slot && (
                        <div className="mb-5 p-4 bg-[#0099D6]/5 border border-[#0099D6]/30 rounded-lg flex items-start gap-3">
                          <CheckCircle
                            size={22}
                            className="text-[#0099D6] flex-shrink-0 mt-0.5"
                          />
                          <div>
                            <h3 className="font-semibold text-base text-white mb-1">
                              {t(tc.service_recommended, lang)}
                            </h3>
                            <p className="text-sm text-slate-300 leading-relaxed">
                              {t(tc.answers_based, lang)}
                            </p>
                          </div>
                        </div>
                      )}
                      {leafResult.notice && (
                        <div className="mb-5">{renderNotice(leafResult.notice)}</div>
                      )}
                      {!slot ? (
                        <AppointmentCalendar
                          onSelectSlot={handleSelectSlot}
                          typeAlias={leafResult.typeAlias}
                        />
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => setSlot(null)}
                            className="flex items-center gap-2 mb-5 text-[#0099D6] hover:underline text-sm font-medium"
                            data-testid="configurator-change-slot"
                          >
                            <ArrowLeft size={16} />
                            {t(tc.change_slot, lang)}
                          </button>
                          <AppointmentForm
                            slot={slot}
                            typeAlias={leafResult.typeAlias}
                            importantNotice={leafResult.notice || null}
                            onSuccess={handleBookingSuccess}
                          />
                        </>
                      )}
                    </motion.div>
                  )}

                {/* Confirmation RDV */}
                {!loading && !error && confirmation && slot && (
                  <motion.div
                    key="confirmed"
                    {...slideRight}
                    className="text-center py-6"
                    data-testid="configurator-confirmed"
                  >
                    <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-400/30 flex items-center justify-center mx-auto mb-5">
                      <CheckCircle className="w-8 h-8 text-emerald-400" />
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
                      {t(tc.appointment_confirmed, lang)}
                    </h2>
                    <p className="text-slate-300 mb-2">
                      {t(tc.slot_intro, lang)}{' '}
                      <strong className="text-[#0099D6]">
                        {new Date(slot.start).toLocaleString(getLocale(lang), {
                          weekday: 'long',
                          day: 'numeric',
                          month: 'long',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </strong>{' '}
                      {t(tc.slot_validated, lang)}
                    </p>
                    <p className="text-slate-400 text-sm mb-6">
                      {t(tc.email_sent, lang)}
                    </p>
                    <button
                      type="button"
                      onClick={handleRestart}
                      className="bg-[#0099D6] hover:bg-[#0284C7] text-white font-semibold py-3 px-6 rounded-lg shadow-[0_0_20px_rgba(0,153,214,0.25)] hover:shadow-[0_0_30px_rgba(0,153,214,0.4)] transition-all hover:scale-[1.02] active:scale-95"
                    >
                      {t(tc.new_diagnostic, lang)}
                    </button>
                  </motion.div>
                )}

                {/* Feuille : callback (redirection vers /contact) */}
                {!loading && !error && leafResult && leafResult.type === 'callback' && (
                  <motion.div
                    key={stepKey}
                    {...slideRight}
                    data-testid="configurator-callback"
                  >
                    <div className="mb-5 p-4 bg-[#0099D6]/5 border border-[#0099D6]/30 rounded-lg flex items-start gap-3">
                      <Phone size={22} className="text-[#0099D6] flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="font-semibold text-base text-white mb-1">
                          {t(tc.callback_title, lang)}
                        </h3>
                        <p className="text-sm text-slate-300 leading-relaxed">
                          {t(tc.callback_text, lang)}
                        </p>
                      </div>
                    </div>
                    {leafResult.notice && (
                      <div className="mb-5">{renderNotice(leafResult.notice)}</div>
                    )}
                    <Link
                      to="/contact?demande=rappel"
                      className="bg-[#0099D6] hover:bg-[#0284C7] text-white font-semibold py-3 px-6 rounded-lg w-full sm:w-auto transition-all hover:scale-[1.02] active:scale-95 inline-flex items-center justify-center gap-2 shadow-[0_0_20px_rgba(0,153,214,0.25)] hover:shadow-[0_0_30px_rgba(0,153,214,0.4)]"
                      data-testid="configurator-open-callback"
                    >
                      <Phone size={18} />
                      {t(tc.callback_cta, lang)}
                    </Link>
                  </motion.div>
                )}

                {/* Feuille : formulaire externe */}
                {!loading && !error && leafResult && leafResult.type === 'external_form' && (
                  <motion.div
                    key={stepKey}
                    {...slideRight}
                    data-testid="configurator-external"
                  >
                    <div className="mb-5 p-4 bg-[#0099D6]/5 border border-[#0099D6]/30 rounded-lg flex items-start gap-3">
                      <ArrowRight size={22} className="text-[#0099D6] flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="font-semibold text-base text-white mb-1">
                          {t(tc.form_dedicated, lang)}
                        </h3>
                        <p className="text-sm text-slate-300 leading-relaxed">
                          {t(tc.form_dedicated_text, lang)}
                        </p>
                      </div>
                    </div>
                    {leafResult.notice && (
                      <div className="mb-5">{renderNotice(leafResult.notice)}</div>
                    )}
                    <Link
                      to={resolveFormUrl(leafResult.formId)}
                      className="bg-[#0099D6] hover:bg-[#0284C7] text-white font-semibold py-3 px-6 rounded-lg w-full sm:w-auto transition-all hover:scale-[1.02] active:scale-95 inline-flex items-center justify-center gap-2 shadow-[0_0_20px_rgba(0,153,214,0.25)] hover:shadow-[0_0_30px_rgba(0,153,214,0.4)]"
                      data-testid="configurator-go-form"
                    >
                      {t(tc.continue_to_form, lang)}
                      <ArrowRight size={18} />
                    </Link>
                  </motion.div>
                )}

                {/* Feuille B2B : formulaire rappel qualifié */}
                {!loading && !error && leafResult && leafResult.kind === 'callback_b2b' && (
                  <motion.div
                    key={stepKey}
                    {...slideRight}
                    data-testid="configurator-callback-b2b"
                  >
                    <CallbackB2BForm
                      contextTag={leafResult.context_tag}
                      treePath={treePath.join(' → ')}
                    />
                  </motion.div>
                )}

                {/* Feuille B2B : urgence téléphonique */}
                {!loading && !error && leafResult && leafResult.kind === 'emergency_phone_cta' && (
                  <motion.div
                    key={stepKey}
                    {...slideRight}
                    data-testid="configurator-emergency-phone"
                  >
                    <EmergencyPhoneCTA treePath={treePath.join(' → ')} />
                  </motion.div>
                )}

                {/* Fallback feuille inconnue */}
                {!loading && !error && leafResult && leafResult.type === 'unknown' && (
                  <motion.div
                    key="unknown"
                    {...slideRight}
                    className="text-center py-8"
                    data-testid="configurator-unknown"
                  >
                    <p className="text-slate-300 mb-4">
                      {t(tc.branch_finalizing, lang)}
                    </p>
                    <Link
                      to="/contact"
                      className="bg-[#0099D6] hover:bg-[#0284C7] text-white font-semibold py-3 px-6 rounded-lg transition-all hover:scale-[1.02] active:scale-95 inline-block"
                    >
                      {t(tc.contact_us, lang)}
                    </Link>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Lien echappatoire en bas de page */}
            {!loading && !error && (
              <div className="text-center mt-6">
                <Link
                  to="/prendre-rdv"
                  className="text-sm text-slate-400 hover:text-[#0099D6] underline"
                >
                  {t(tc.see_all_types, lang)}
                </Link>
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
}
